import { UploadFile } from '@anispi-reporting-system/types';
import { BackupOutlined } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getFileKindResult, uploadFiles } from '../../lib/Api';
import { ErrorMessages } from '../ErrorMessages';
import { LoadingDialog } from '../LoadingDialog';
import { SnackbarMessageProps } from '../SnackbarMessage';

interface Props {
  setSnackbarMessage: React.Dispatch<React.SetStateAction<Omit<SnackbarMessageProps, 'onClose'>>>;
  setUploadFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

const UploadCsvFiles: FunctionComponent<Props> = ({ setSnackbarMessage, setUploadFiles }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<boolean | undefined>(undefined);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setLoading(true);
      setErrorMessages([]);

      try {
        if (acceptedFiles.length > 0) {
          const uploadFileInfo = await uploadFiles({ fileNum: acceptedFiles.length });

          if (!uploadFileInfo || uploadFileInfo.success === false) {
            setResult(false);

            const uploadFileInfoErrorMessages = ['ファイルのアップロードに失敗しました。'];

            if (uploadFileInfo && uploadFileInfo.errorMessages) {
              uploadFileInfoErrorMessages.push(...uploadFileInfo.errorMessages);
            }

            setErrorMessages(uploadFileInfoErrorMessages);
            return;
          }

          const response = await Promise.allSettled(
            acceptedFiles.map((acceptedFile, index) => fetch(uploadFileInfo.files[index].url, { body: acceptedFile, method: 'PUT' })),
          );

          const mergeResponse = response.map((res, index) => ({
            fileName: acceptedFiles[index].name,
            key: uploadFileInfo.files[index].fileName,
            status: res.status,
          }));

          const fileKindResult = await getFileKindResult({ fileNames: uploadFileInfo.files.map(({ fileName }) => fileName) });

          // 事業所番号,会社Noの取得,サービス種別の取得
          const uploadedFiles = mergeResponse
            .filter(({ status }) => status === 'fulfilled')
            .map(({ fileName, key }) => {
              const findFileKindResult = fileKindResult.files.find(file => file.fileName === key);

              return {
                check: undefined,
                companyNo: findFileKindResult?.companyNo ?? '',
                errorMessages: [],
                fileName,
                key,
                officeNo: findFileKindResult?.officeNo ?? '',
                sent: false,
                service: findFileKindResult?.service ?? '',
              };
            });

          const uploadErrorMessages: string[] = [];
          mergeResponse.filter(({ status }) => status === 'rejected').forEach(({ fileName }) => uploadErrorMessages.push(fileName));

          if (uploadErrorMessages.length > 0) {
            setErrorMessages(['以下のファイルのアップロードに失敗しました', ...uploadErrorMessages]);
          }

          if (uploadedFiles.length > 0) {
            setUploadFiles(uploadedFiles);
          }
        } else {
          setSnackbarMessage({
            message: '300ファイル以内でアップロードファイルを選択してください。',
            open: true,
            severity: 'error',
          });
        }
      } catch {
        setSnackbarMessage({
          message: 'エラーが発生しました。ファイルのアップロードに失敗しました。',
          open: true,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [setUploadFiles, setSnackbarMessage],
  );

  const { getRootProps, getInputProps } = useDropzone({ accept: { 'text/csv': ['.csv'] }, maxFiles: 300, onDrop });

  return (
    <>
      <Grid container spacing={2} rowSpacing={3}>
        {result === false && errorMessages.length > 0 && (
          <Grid item xs={12}>
            <ErrorMessages errorMessages={errorMessages} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box
            {...getRootProps()}
            className="flex h-60 w-full flex-col items-center justify-center rounded-md border-2 border-dotted border-gray-500 bg-gray-100 p-2 text-center"
          >
            <input {...getInputProps()} />
            <p>こちらに国保連伝送用CSVファイルをドロップしてください。(300件まで)</p>
            <BackupOutlined fontSize="large" />
          </Box>
        </Grid>
      </Grid>
      <LoadingDialog open={loading} title="ファイルアップロード中" message="ファイルアップロード中です。しばらくお待ちください。" />
    </>
  );
};

export { UploadCsvFiles };
